import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { UserPlus, Mail, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface InviteUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onInviteSuccess: () => void;
}

const ROLES = [
          { value: 'viewer', label: 'Viewer', description: 'Can view data but cannot make changes' },
          { value: 'data_entry', label: 'Data Entry', description: 'Can view and enter data' },
          { value: 'coordinator', label: 'Coordinator', description: 'Can manage data and some settings' },
          { value: 'admin', label: 'Administrator', description: 'Full access to workspace management' },
];

export function InviteUserModal({ open, onOpenChange, workspaceId, onInviteSuccess }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleInvite = async () => {
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    if (!role) {
      toast({
        title: "Role required", 
        description: "Please select a role for the user",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('🚀 Sending invitation...', { workspaceId, email, role });
      
      // Use unified workspace-invitations endpoint
      const response = await supabase.functions.invoke('workspace-invitations', {
        body: {
          action: 'create',
          workspace_id: workspaceId,
          email: email.trim(),
          role_code: role,
          custom_message: message.trim() || undefined
        }
      });
      
      const data = response.data;
      const error = response.error;

      console.log('📥 Invitation response:', { data, error });

      if (error) {
        console.error('❌ Supabase function error:', error);
        throw new Error(error.message || 'Failed to send invitation');
      }
      
      if (data?.error) {
        console.error('❌ API error:', data.error);
        throw new Error(data.error);
      }
      
      if (data?.success || (data?.ok && data?.action === 'create')) {
        toast({
          title: "Invitation sent!",
          description: `Invitation sent to ${email} with ${role} role`,
        });
        
        // Reset form
        setEmail('');
        setRole('viewer');
        setMessage('');
        
        onInviteSuccess();
        onOpenChange(false);
      } else {
        throw new Error('Unexpected response from server');
      }
      
    } catch (error: any) {
      console.error('💥 Invitation error:', error);
      
      let errorMessage = 'Please try again later';
      
      if (error.message?.includes('Unauthorized')) {
        errorMessage = 'You don\'t have permission to send invitations';
      } else if (error.message?.includes('already invited')) {
        errorMessage = 'This user is already invited';
      } else if (error.message?.includes('already a member')) {
        errorMessage = 'This user is already a member';
      } else if (error.message?.includes('not found')) {
        errorMessage = 'Organization not found';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Failed to send invitation",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Invite User to Workspace
          </DialogTitle>
          <DialogDescription>
            Send an invitation to collaborate on this workspace. They'll receive an email with instructions to join.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="role" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Role
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((roleOption) => (
                  <SelectItem key={roleOption.value} value={roleOption.value}>
                    <div>
                      <div className="font-medium">{roleOption.label}</div>
                      <div className="text-sm text-muted-foreground">{roleOption.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">
              Personal Message (Optional)
            </Label>
            <Textarea
              id="message"
              placeholder="Add a personal message to the invitation..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleInvite} disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}